import SubcontractorService from '../../services/SubcontractorService';
import { ReduxThunkType } from '../../types/ReduxThunkType';
import { ExternalUserStatus } from '../../enums/ExternalUserStatus';
import { RestErrorResponse } from '../../types/response/RestErrorResponse';
import { Actions } from './actions';
import { Contact } from './types';
import { NotificationsEmail, SubcontractorDetailsRequest } from '../project/types';

export const getExternalUserContacts =
  (userId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.getExternalUserContacts());
      const response = await SubcontractorService.getExternalUserContacts(userId);
      dispatch(Actions.getExternalUserContactsSuccess(response.data.content));
    } catch (error) {
      dispatch(Actions.getExternalUserContactsError(error as RestErrorResponse));
    }
  };

export const addExternalUserContact =
  (externalUserContact: Contact, userId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.addExternalUserContact());
      const response = await SubcontractorService.addExternalUserContact(externalUserContact, userId);
      dispatch(Actions.addExternalUserContactSuccess());
      return Promise.resolve(response);
    } catch (error) {
      dispatch(Actions.getExternalUserContactsError(error as RestErrorResponse));
      return Promise.resolve(error);
    }
  };

export const updateExternalUserContact =
  (externalUserContact: Contact, userId: number, contactId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.updateExternalUserContact());
      await SubcontractorService.updateExternalUserContact(externalUserContact, userId, contactId);
      dispatch(Actions.updateExternalUserContactSuccess());
    } catch (error) {
      dispatch(Actions.getExternalUserContactsError(error as RestErrorResponse));
    }
  };

export const changeUserAccountStatus =
  (userId: number, status: boolean): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.changeUserAccountStatus());
      await SubcontractorService.changeUserAccountStatus(userId, status);
      dispatch(Actions.changeUserAccountStatusSuccess());
    } catch (error) {
      dispatch(Actions.changeUserAccountStatusError(error as RestErrorResponse));
    }
  };

export const getExternalUsersByProjectId =
  (projectId: number, status: ExternalUserStatus[]): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.getExternalUsersByProjectId());
      const result = await SubcontractorService.getExternalUsersByProjectId(projectId, status);
      dispatch(Actions.getExternalUsersByProjectIdSuccess(result.data.content));
    } catch (error) {
      dispatch(Actions.getExternalUsersByProjectIdError(error as RestErrorResponse));
    }
  };

export const changeSubcontractorStatus =
  (subcontractorId: number, projectId: number, status: ExternalUserStatus): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.changeSubcontractorStatus());
      await SubcontractorService.changeSubcontractorStatus(subcontractorId, projectId, status);
      dispatch(Actions.changeSubcontractorStatusSuccess());
    } catch (error) {
      dispatch(Actions.changeSubcontractorStatusError(error as RestErrorResponse));
    }
  };

export const changeSubcontractorEmail =
  (subcontractorId: number, email: string): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.changeSubcontractorEmail());
      await SubcontractorService.changeSubcontractorEmail(subcontractorId, email);
      dispatch(Actions.changeSubcontractorEmailSuccess());
    } catch (error) {
      dispatch(Actions.changeSubcontractorEmailError(error as RestErrorResponse));
    }
  };

export const createSubcontractorAccount =
  (data: SubcontractorDetailsRequest): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.createSubcontractorAccount());
      await SubcontractorService.createSubcontractorAccount(data);
      dispatch(Actions.createSubcontractorAccountSuccess());
    } catch (error) {
      dispatch(Actions.createSubcontractorAccountError(error as RestErrorResponse));
    }
  };

export const saveSubcontractorNotificationEmail =
  (data: NotificationsEmail, subcontractorId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.saveSubcontractorNotificationEmail());
      await SubcontractorService.saveSubcontractorNotificationEmail(data, subcontractorId);
      dispatch(Actions.saveSubcontractorNotificationEmailSuccess(data));
    } catch (error) {
      dispatch(Actions.saveSubcontractorNotificationEmailError(error as RestErrorResponse));
    }
  };

export const editSubcontractorAccount =
  (data: SubcontractorDetailsRequest, subcontractorId: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.editSubcontractorAccount());
      await SubcontractorService.editSubcontractorAccount(subcontractorId, data);
      dispatch(Actions.editSubcontractorAccountSuccess(data));
    } catch (error) {
      dispatch(Actions.editSubcontractorAccountError(error as RestErrorResponse));
    }
  };

export const getSubcontractorNotificationEmailSettings =
  (subcontractorID: number): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.getSubcontractorNotificationEmailSettings());
      const response = await SubcontractorService.getSubcontractorNotificationEmailSettings(subcontractorID);
      dispatch(Actions.getSubcontractorNotificationEmailSettingsSuccess(response.data.content));
    } catch (error) {
      dispatch(Actions.getSubcontractorNotificationEmailSettingsError(error as RestErrorResponse));
    }
  };

export const changeSubcontractorVisibility =
  (projectId: number, subcontractorId: number, isHidden: boolean): ReduxThunkType<void> =>
  async (dispatch) => {
    try {
      dispatch(Actions.changeSubcontractorVisibility());
      await SubcontractorService.changeSubcontractorVisibility(projectId, subcontractorId, isHidden);
      dispatch(Actions.changeSubcontractorVisibilitySuccess());
      dispatch(
        getExternalUsersByProjectId(projectId, [
          ExternalUserStatus.Active,
          ExternalUserStatus.Locked,
          ExternalUserStatus.Invited,
          ExternalUserStatus.Unblocked
        ])
      );
    } catch (error) {
      dispatch(Actions.changeSubcontractorVisibilityError(error as RestErrorResponse));
    }
  };
